import React from 'react';
import {
    alpha,
    Box,
    Chip,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {
    AutoAwesome,
    Inventory2,
    Link as LinkIcon,
    PictureAsPdf,
    TableRows,
} from '@mui/icons-material';

const nutritionRows = [
    {
        parameter: 'Energy',
        value: '380 kcal',
        status: 'Extracted',
        tone: 'success',
    },
    {
        parameter: 'Protein',
        value: '80 g',
        status: 'Extracted',
        tone: 'success',
    },
    {
        parameter: 'Carbohydrate',
        value: '8.0 g',
        status: 'Extracted',
        tone: 'success',
    },
    {
        parameter: 'Sugars',
        value: '6.0 g',
        status: 'Extracted',
        tone: 'success',
    },
    {
        parameter: 'Fat',
        value: '5.5 g',
        status: 'Extracted',
        tone: 'success',
    },
    {
        parameter: 'Salt',
        value: '1.2 g',
        status: 'Review',
        tone: 'warning',
    },
];

const attributes = [
    { label: 'Moisture', value: '< 6%' },
    { label: 'Fat', value: '5.5 g' },
    { label: 'pH', value: '6.2' },
];

const sourceSections = [
    'Ingredient description',
    'Nutritional composition',
    'Allergen declaration',
    'Technical specifications',
];

function StatusChip({ status, tone }) {
    const isWarning = tone === 'warning';

    return (
        <Box
            sx={{
                px: 0.65,
                py: 0.25,
                borderRadius: 999,
                fontSize: 9.4,
                fontWeight: 900,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                color: isWarning ? 'warning.dark' : 'success.dark',
                bgcolor: (theme) =>
                    isWarning
                        ? alpha(theme.palette.warning.main, 0.14)
                        : alpha(theme.palette.success.main, 0.12),
                border: '1px solid',
                borderColor: (theme) =>
                    isWarning
                        ? alpha(theme.palette.warning.main, 0.3)
                        : alpha(theme.palette.success.main, 0.28),
            }}
        >
            {status}
        </Box>
    );
}

export default function SupplierTdsImporterVisual() {
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 300,
                p: 1.6,
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    width: '100%',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        px: 1.7,
                        py: 1.2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <PictureAsPdf sx={{ fontSize: 16 }} />
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                Supplier data and TDS importer
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.2 }}
                            >
                                Extract supplier specifications into structured ingredient data
                            </Typography>
                        </Box>
                    </Stack>

                    <Chip
                        label="AI-assisted"
                        size="small"
                        sx={{
                            height: 23,
                            fontSize: 10.5,
                            fontWeight: 800,
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            color: 'primary.main',
                        }}
                    />
                </Stack>

                {/* Body */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '0.95fr 1.35fr' },
                        minHeight: 250,
                        alignItems: 'stretch',
                    }}
                >
                    {/* Left: PDF source */}
                    <Box
                        sx={{
                            p: 1.7,
                            borderRight: { sm: '1px solid rgba(15, 23, 42, 0.08)' },
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.016),
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                mb: 1.1,
                                fontWeight: 800,
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: 0.4,
                            }}
                        >
                            Supplier PDF
                        </Typography>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.25,
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                mb: 1.15,
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 1.2 }}>
                                <Box
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                        color: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <PictureAsPdf sx={{ fontSize: 15 }} />
                                </Box>

                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            fontWeight: 900,
                                            fontSize: 11,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        WPC80_TDS_supplier.pdf
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', fontSize: 10 }}
                                    >
                                        Technical Data Sheet
                                    </Typography>
                                </Box>
                            </Stack>

                            <Stack
                                spacing={0.7}
                                sx={{
                                    flex: 1,
                                    justifyContent: 'space-evenly',
                                }}
                            >
                                {sourceSections.map((section) => (
                                    <Stack
                                        key={section}
                                        direction="row"
                                        spacing={0.6}
                                        alignItems="center"
                                        sx={{
                                            px: 0.85,
                                            py: 0.65,
                                            borderRadius: 1.4,
                                            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.055),
                                        }}
                                    >
                                        <TableRows sx={{ fontSize: 13, color: 'primary.main' }} />

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: 10.2,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {section}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Paper>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1,
                                borderRadius: 2,
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.045),
                                flexShrink: 0,
                            }}
                        >
                            <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                <AutoAwesome sx={{ fontSize: 16, color: 'primary.main', mt: 0.1 }} />

                                <Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            fontWeight: 900,
                                            fontSize: 10.6,
                                        }}
                                    >
                                        AI extraction
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            fontSize: 10.2,
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        Key values are extracted and prepared for manual validation.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Box>

                    {/* Right: extracted ingredient data */}
                    <Box
                        sx={{
                            p: 1.45,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 0.9 }}
                        >
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <Inventory2 sx={{ fontSize: 16, color: 'primary.main' }} />

                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 800,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.4,
                                    }}
                                >
                                    Extracted nutrition data
                                </Typography>
                            </Stack>

                            <StatusChip status="Validation required" tone="warning" />
                        </Stack>

                        <Paper
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                bgcolor: 'background.paper',
                                mb: 0.95,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 0.85fr auto',
                                    columnGap: 1,
                                    px: 1.15,
                                    py: 0.72,
                                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                {['Nutrient', 'Per 100 g', 'Status'].map((head) => (
                                    <Typography
                                        key={head}
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontWeight: 800, fontSize: 10.1 }}
                                    >
                                        {head}
                                    </Typography>
                                ))}
                            </Box>

                            {nutritionRows.map((row, index) => (
                                <Box
                                    key={row.parameter}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 0.85fr auto',
                                        columnGap: 1,
                                        alignItems: 'center',
                                        px: 1.15,
                                        py: 0.64,
                                        borderBottom:
                                            index !== nutritionRows.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                        bgcolor:
                                            row.tone === 'warning'
                                                ? (theme) => alpha(theme.palette.warning.main, 0.045)
                                                : 'background.paper',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: 10.7,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {row.parameter}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            fontWeight: 800,
                                            fontSize: 10.7,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {row.value}
                                    </Typography>

                                    <StatusChip status={row.status} tone={row.tone} />
                                </Box>
                            ))}
                        </Paper>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1,
                                borderRadius: 2,
                                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025),
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={0.65}
                                alignItems="center"
                                sx={{ mb: 0.65 }}
                            >
                                <LinkIcon sx={{ fontSize: 15, color: 'primary.main' }} />

                                <Typography variant="caption" sx={{ fontWeight: 900 }}>
                                    Technical attributes
                                </Typography>
                            </Stack>

                            <Stack spacing={0.45}>
                                {attributes.map((item) => (
                                    <Stack
                                        key={item.label}
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{
                                            px: 0.7,
                                            py: 0.32,
                                            borderRadius: 1.2,
                                            bgcolor: 'background.paper',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontWeight: 800, fontSize: 9.8 }}
                                        >
                                            {item.label}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            sx={{ fontWeight: 900, fontSize: 9.8 }}
                                        >
                                            {item.value}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Paper>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}